import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';
import { getNowWeekdayAndDate } from '@clearverify/utils';

import { PageLayout, TabItem, TabItemKey, Tabs } from 'src/components/common';
import { InboxEmptyWrapper, InboxItemsList } from 'src/components/inboxItem';
import { InboxPageLayout } from 'src/constants/inbox';
import { searchFieldStyles } from 'src/constants/styles';
import { useScreenInfo, useSearch } from 'src/hooks/common';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useInboxItemParamId, useInboxItems } from 'src/hooks/inboxItem';
import { useCurrentUserPeo } from 'src/hooks/peo';
import { useCurrentUserPeoPayrollProvider } from 'src/hooks/peoPayrollProvider';
import { useTestVerificationRequestsRoute } from 'src/hooks/testVerificationRequests';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { LayoutHeader, LayoutHeaderAction } from './components';
import {
  contentStyles,
  contentWrapperStyles,
  inboxWrapperStyles,
  leftMenuTabsContainerStyles,
  leftMenuWrapperStyles,
  leftMobileMenuWrapperStyles,
  mobileContentStyles,
  mobileContentWrapperStyles,
  paperStyles,
} from './styles';

interface InboxLayoutProps {
  topAction?: LayoutHeaderAction;
}

export const InboxLayout: React.FC<React.PropsWithChildren<InboxLayoutProps>> = ({
  topAction,
  children: content,
}) => {
  const navigate = useNavigate();

  const { setSearchValue } = useSearch();
  const { isMobile } = useScreenInfo();

  const { isPlatformAdmin } = useCurrentUserPermissions();

  const { isTestRequestsRoute } = useTestVerificationRequestsRoute();

  const { peoPayrollProvider, loading: peoPayrollProviderLoading } =
    useCurrentUserPeoPayrollProvider();
  const { peo, loading: peoLoading } = useCurrentUserPeo();
  const { employer, loading: employerLoading } = useCurrentUserEmployer();

  const loading = peoPayrollProviderLoading || peoLoading || employerLoading;

  const homeSectionName =
    peoPayrollProvider?.displayName ?? peo?.displayName ?? employer?.displayName ?? 'Home';
  const titleName = isPlatformAdmin ? 'Inbox' : homeSectionName;

  const { isEmptyInbox } = useInboxItems();
  const inboxItemId = useInboxItemParamId();

  const onChangeTabs = React.useCallback(
    (value: TabItemKey) => {
      if (value === InboxPageLayout.TestRequests) {
        navigate(appRoutes.inbox.inboxTestRequests);
      } else {
        navigate(appRoutes.inbox.index);
      }
    },
    [navigate],
  );

  const renderInbox = React.useCallback(() => {
    const tabsValue = isTestRequestsRoute ? InboxPageLayout.TestRequests : InboxPageLayout.Inbox;

    return (
      <React.Fragment>
        <LayoutHeader
          searchProps={{
            onSearch: setSearchValue,
            searchFieldStyles,
          }}
          actionProps={{
            action: topAction,
          }}
        />
        <Box sx={inboxWrapperStyles}>
          {isPlatformAdmin ? (
            <Tabs
              tabs={inboxTabs}
              containerProps={{ sx: leftMenuTabsContainerStyles }}
              additionalTabsContainerStyles={{ marginBottom: 0 }}
              contentStyles={leftMenuWrapperStyles}
              value={tabsValue}
              onChange={onChangeTabs}
            />
          ) : (
            <Box sx={leftMenuWrapperStyles}>
              <InboxItemsList />
            </Box>
          )}
          {isEmptyInbox ? <InboxEmptyWrapper /> : <Box sx={contentWrapperStyles}>{content}</Box>}
        </Box>
      </React.Fragment>
    );
  }, [
    content,
    isPlatformAdmin,
    isEmptyInbox,
    onChangeTabs,
    setSearchValue,
    topAction,
    isTestRequestsRoute,
  ]);

  const renderMobileInbox = React.useCallback(() => {
    if (inboxItemId) {
      return (
        <Box sx={inboxWrapperStyles}>
          {isEmptyInbox ? (
            <InboxEmptyWrapper />
          ) : (
            <Box sx={mobileContentWrapperStyles}>{content}</Box>
          )}
        </Box>
      );
    }

    return (
      <Box sx={inboxWrapperStyles}>
        <Box sx={leftMobileMenuWrapperStyles}>
          <InboxItemsList />
        </Box>
      </Box>
    );
  }, [content, inboxItemId, isEmptyInbox]);

  if (isMobile) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        {!inboxItemId && (
          <LayoutHeader
            searchProps={{
              onSearch: setSearchValue,
              searchContainerStyles: {
                width: '100%',
              },
              searchFieldStyles: {
                width: '100%',
              },
              disabled: isEmptyInbox,
            }}
            actionProps={{
              action: topAction,
            }}
          />
        )}
        <Box sx={mobileContentStyles}>{renderMobileInbox()}</Box>
      </Box>
    );
  }

  return (
    <PageLayout
      titleContent={loading ? <Skeleton width={150} height={40} /> : titleName}
      topRightContent={<Typography color="grey.500">{getNowWeekdayAndDate()}</Typography>}
      paperSx={paperStyles}
    >
      <Box sx={contentStyles}>{renderInbox()}</Box>
    </PageLayout>
  );
};

const inboxTabs: TabItem[] = [
  {
    key: 'Inbox',
    label: InboxPageLayout.Inbox,
    component: <InboxItemsList />,
  },
  {
    key: 'Test Requests',
    label: InboxPageLayout.TestRequests,
    component: <InboxItemsList />,
  },
];
